import classNames from 'classnames';
import { useMemo } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import styles from './individual-selector.module.scss';

export interface IndividualSelectorProps {
  /** The class name to apply */
  className?: string;
  /** Indicates the direction of the homebound and outbound product */
  direction?: Content.Components['ProductTransportDirection'];
  /** An event that is triggered on click */
  onClick?: () => void;
  /** The passenger data */
  passenger: Content.Components['GenericProductPassengerDto'];
}

const IndividualSelector = ({
  className,
  direction = 'default',
  onClick,
  passenger,
}: IndividualSelectorProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();
  const { firstName, lastName, placeholders } = passenger.passenger;

  const headline =
    !firstName && !lastName
      ? `${placeholders.firstName} ${placeholders.lastName}`
      : `${firstName} ${lastName}`;

  const relevantProducts = passenger.relevantPassengerBookingProducts;

  const filteredRelevantProducts = useMemo(() => {
    return relevantProducts.filter(product => product.direction === direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relevantProducts]);

  const individualProduct = filteredRelevantProducts[0];
  const isSelected = individualProduct.isSelected;
  const isDisabled = passenger.isDisabled;

  return (
    <Link
      className={classNames(styles.component, className, {
        [styles.isSelected]: isSelected,
        [styles.isDisabled]: isDisabled,
      })}
      onClick={onClick}
    >
      <div className={styles.selectIcon}>
        {isSelected && <Icon name="CheckMark" size={20} />}
      </div>

      <div className={styles.content}>
        {headline && (
          <div className={styles.headline}>
            <Typography tag="h3" tagStyle="bodyMediumBold">
              {headline}
            </Typography>
          </div>
        )}

        {!isDisabled && individualProduct?.price >= 0 && (
          <div className={styles.price}>
            <Typography tagStyle="bodySmall">
              {individualProduct.price === 0
                ? t('items.modules.bookingFlow.free')
                : currencyFormatter(individualProduct.price)}
            </Typography>
          </div>
        )}

        {isDisabled && passenger.disabledText && (
          <div className={styles.price}>
            <Typography tagStyle="bodySmall">
              {passenger.disabledText}
            </Typography>
          </div>
        )}
      </div>
    </Link>
  );
};

IndividualSelector.displayName = 'IndividualSelector';

export default IndividualSelector;
